/**
 * MapNavigationControls Component
 *
 * Phase 5: Map Controls & Legend Implementation
 * Phase 7: Responsive & Accessibility Enhancements
 *
 * Provides navigation controls for the MapComponent:
 * - Zoom in/out buttons with icons
 * - Compass/north indicator that rotates with bearing
 * - Fullscreen toggle button
 * - "Fit to data" button (calculates bounds from sensor data)
 * - Scale indicator showing distance
 *
 * Phase 7 Responsive Design:
 * - Mobile (≤599px): Collapsed compact mode, minimal controls
 * - Tablet (600-1023px): Standard compact mode
 * - Desktop (≥1024px): Full size controls
 *
 * Phase 7 Accessibility (WCAG 2.1 AA):
 * - Full keyboard navigation (Tab, Enter, Space, Arrow keys, +/-)
 * - ARIA labels for all interactive elements
 * - Screen reader announcements via aria-live regions
 * - Focus indicators with visible focus rings
 * - Touch gesture support via react-map-gl
 *
 * Created: January 2026
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Plus,
  Minus,
  Compass,
  Maximize,
  Minimize,
  Target,
  Navigation2,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  MapControlsConfig,
  ControlPosition,
  SensorDataPoint,
  MapPin as MapPinType,
  DEFAULT_CONTROLS_CONFIG,
} from '../MapComponent.types';
import {
  mapControlButtonClass,
  mapControlDividerClass,
  mapControlSurfaceClass,
  mapControlText,
} from './mapControlStyles';

// ============================================================================
// Types
// ============================================================================

export interface MapNavigationControlsProps {
  /** Current zoom level (0-22) */
  zoom: number;
  /** Current bearing/rotation in degrees */
  bearing: number;
  /** Minimum zoom level */
  minZoom?: number;
  /** Maximum zoom level */
  maxZoom?: number;
  /** Callback for zoom changes */
  onZoomChange: (zoom: number) => void;
  /** Callback for bearing changes (reset to north) */
  onBearingChange: (bearing: number) => void;
  /** Callback for fit to bounds */
  onFitToBounds: () => void;
  /** Sensor data for calculating bounds */
  sensorData?: SensorDataPoint[];
  /** Pin data for calculating bounds */
  pins?: MapPinType[];
  /** Controls configuration */
  config?: Partial<MapControlsConfig>;
  /** Container width for responsive sizing */
  containerWidth?: number;
  /** Container height for scaling controls */
  containerHeight?: number;
  /** Whether map is in fullscreen mode */
  isFullscreen?: boolean;
  /** Callback for fullscreen toggle */
  onFullscreenToggle?: () => void;
  /** Additional CSS class */
  className?: string;
}

// ============================================================================
// Breakpoint Constants (from CLAUDE.md)
// ============================================================================

const BREAKPOINTS = {
  mobile: 599,   // ≤599px
  tablet: 1023,  // 600-1023px
  desktop: 1024, // ≥1024px
} as const;

type ViewportSize = 'mobile' | 'tablet' | 'desktop';

// ============================================================================
// Position Styles
// ============================================================================

const getPositionStyles = (position: ControlPosition): React.CSSProperties => {
  const base: React.CSSProperties = {
    position: 'absolute',
    zIndex: 10,
  };

  switch (position) {
    case 'top-left':
      return { ...base, top: 10, left: 10 };
    case 'top-right':
      return { ...base, top: 10, right: 10 };
    case 'bottom-left':
      return { ...base, bottom: 10, left: 10 };
    case 'bottom-right':
      return { ...base, bottom: 10, right: 10 };
    default:
      return { ...base, top: 10, right: 10 };
  }
};

// ============================================================================
// Accessibility Styles
// ============================================================================

const focusRingStyles = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';
const highContrastStyles = 'text-muted-foreground group-hover:text-foreground';

// ============================================================================
// Main Component
// ============================================================================

export const MapNavigationControls: React.FC<MapNavigationControlsProps> = ({
  zoom,
  bearing,
  minZoom = 0,
  maxZoom = 22,
  onZoomChange,
  onBearingChange,
  onFitToBounds,
  sensorData = [],
  pins = [],
  config = {},
  containerWidth,
  containerHeight,
  isFullscreen = false,
  onFullscreenToggle,
  className,
}) => {
  // Merge config with defaults
  const mergedConfig: MapControlsConfig = {
    ...DEFAULT_CONTROLS_CONFIG,
    ...config,
  };

  // Refs for keyboard navigation
  const controlsRef = useRef<HTMLDivElement>(null);
  const announceRef = useRef<HTMLDivElement>(null);

  // State
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [lastAnnouncement, setLastAnnouncement] = useState('');

  // Determine viewport size
  const getViewportSize = useCallback((): ViewportSize => {
    if (containerWidth === undefined) return 'desktop';
    if (containerWidth <= BREAKPOINTS.mobile) return 'mobile';
    if (containerWidth <= BREAKPOINTS.tablet) return 'tablet';
    return 'desktop';
  }, [containerWidth]);

  const viewportSize = getViewportSize();

  // Calculate scale factor based on container height
  // Scale down when height is below 300px (base height)
  const BASE_HEIGHT = 300;
  const MIN_SCALE = 0.5;
  const heightScale = containerHeight !== undefined && containerHeight < BASE_HEIGHT
    ? Math.max(MIN_SCALE, containerHeight / BASE_HEIGHT)
    : 1;

  // Determine compact mode based on viewport or small height
  const isCompact = mergedConfig.compact || viewportSize !== 'desktop' || heightScale < 0.8;
  const isMobile = viewportSize === 'mobile';

  // Button size based on viewport
  const buttonSize = isCompact ? 'h-8 w-8' : 'h-10 w-10';
  const iconSize = isCompact ? 16 : 20;

  // ---------------------------------------------------------------------------
  // Screen Reader Announcement Helper
  // ---------------------------------------------------------------------------

  const announce = useCallback((message: string) => {
    setLastAnnouncement(message);
    // Clear announcement after it's been read
    setTimeout(() => setLastAnnouncement(''), 1000);
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(maxZoom, zoom + 1);
    onZoomChange(newZoom);
    announce(`Zoomed in to level ${Math.round(newZoom)}`);
  }, [zoom, maxZoom, onZoomChange, announce]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(minZoom, zoom - 1);
    onZoomChange(newZoom);
    announce(`Zoomed out to level ${Math.round(newZoom)}`);
  }, [zoom, minZoom, onZoomChange, announce]);

  const handleResetNorth = useCallback(() => {
    onBearingChange(0);
    announce('Map rotated to face north');
  }, [onBearingChange, announce]);

  const totalDataPoints = sensorData.length + pins.length;

  const handleFitToBounds = useCallback(() => {
    onFitToBounds();
    announce(`Fitted map to show all ${totalDataPoints} data points`);
  }, [onFitToBounds, sensorData.length, pins.length, totalDataPoints, announce]);

  const handleFullscreenToggle = useCallback(() => {
    onFullscreenToggle?.();
    announce(isFullscreen ? 'Exited fullscreen mode' : 'Entered fullscreen mode');
  }, [isFullscreen, onFullscreenToggle, announce]);

  const handleToggleCollapse = useCallback(() => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    announce(newCollapsed ? 'Map controls collapsed' : 'Map controls expanded');
  }, [isCollapsed, announce]);

  // ---------------------------------------------------------------------------
  // Keyboard Navigation
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Handle global keyboard shortcuts for map navigation
    switch (e.key) {
      case '+':
      case '=':
        e.preventDefault();
        handleZoomIn();
        break;
      case '-':
      case '_':
        e.preventDefault();
        handleZoomOut();
        break;
      case 'ArrowUp':
        if (e.shiftKey) {
          e.preventDefault();
          // Shift+Up could pan up (handled by map component)
          announce('Pan up');
        }
        break;
      case 'ArrowDown':
        if (e.shiftKey) {
          e.preventDefault();
          announce('Pan down');
        }
        break;
      case 'ArrowLeft':
        if (e.shiftKey) {
          e.preventDefault();
          announce('Pan left');
        }
        break;
      case 'ArrowRight':
        if (e.shiftKey) {
          e.preventDefault();
          announce('Pan right');
        }
        break;
      case 'n':
      case 'N':
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          handleResetNorth();
        }
        break;
      case 'f':
      case 'F':
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          handleFitToBounds();
        }
        break;
      case 'Escape':
        if (isFullscreen) {
          e.preventDefault();
          handleFullscreenToggle();
        }
        break;
    }
  }, [handleZoomIn, handleZoomOut, handleResetNorth, handleFitToBounds, handleFullscreenToggle, isFullscreen, announce]);

  // ---------------------------------------------------------------------------
  // Effect: Global keyboard listener for zoom shortcuts
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Handle zoom shortcuts
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        handleZoomOut();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleZoomIn, handleZoomOut]);

  // ---------------------------------------------------------------------------
  // Collapsed View (Mobile)
  // ---------------------------------------------------------------------------

  if (isMobile && isCollapsed) {
    return (
      <TooltipProvider>
        {/* Screen reader live region */}
        <div
          ref={announceRef}
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          role="status"
        >
          {lastAnnouncement}
        </div>

        <div
          className={cn(
            'flex items-center gap-1 p-1.5',
            mapControlSurfaceClass,
            className
          )}
          style={getPositionStyles(mergedConfig.position)}
          data-testid="map-navigation-controls"
          role="toolbar"
          aria-label="Map navigation controls (collapsed)"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleCollapse}
                className={cn('h-8 w-8 group', focusRingStyles, mapControlButtonClass)}
                aria-label="Expand map controls"
                aria-expanded={false}
                data-testid="map-controls-expand"
              >
                <ChevronUp size={16} className={highContrastStyles} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Expand controls</p>
            </TooltipContent>
          </Tooltip>

          {/* Quick zoom buttons even when collapsed */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomIn}
                disabled={zoom >= maxZoom}
                className={cn('h-8 w-8 group', focusRingStyles, mapControlButtonClass)}
                aria-label={`Zoom in (current zoom: ${Math.round(zoom)})`}
                data-testid="map-zoom-in-collapsed"
              >
                <Plus size={16} className={highContrastStyles} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Zoom in (+)</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomOut}
                disabled={zoom <= minZoom}
                className={cn('h-8 w-8 group', focusRingStyles, mapControlButtonClass)}
                aria-label={`Zoom out (current zoom: ${Math.round(zoom)})`}
                data-testid="map-zoom-out-collapsed"
              >
                <Minus size={16} className={highContrastStyles} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Zoom out (-)</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  // ---------------------------------------------------------------------------
  // Full Render
  // ---------------------------------------------------------------------------

  return (
    <TooltipProvider>
      {/* Screen reader live region for announcements */}
      <div
        ref={announceRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {lastAnnouncement}
      </div>

      <div
        ref={controlsRef}
        className={cn(
          'flex flex-col gap-1 p-1.5',
          mapControlSurfaceClass,
          'transition-all duration-200 ease-in-out',
          isCompact && 'p-1 gap-0.5',
          className
        )}
        style={{
          ...getPositionStyles(mergedConfig.position),
          transform: heightScale < 1 ? `scale(${heightScale})` : undefined,
          transformOrigin: 'top right',
        }}
        data-testid="map-navigation-controls"
        role="toolbar"
        aria-label="Map navigation controls"
        aria-orientation="vertical"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Mobile: Collapse Button */}
        {isMobile && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleCollapse}
                className={cn(buttonSize, focusRingStyles, 'group', mapControlButtonClass)}
                aria-label="Collapse map controls"
                aria-expanded={true}
                data-testid="map-controls-collapse"
              >
                <ChevronDown size={iconSize} className={highContrastStyles} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Collapse controls</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Zoom In Button */}
        {mergedConfig.showZoom && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomIn}
                disabled={zoom >= maxZoom}
                className={cn(
                  buttonSize,
                  focusRingStyles,
                  'group',
                  mapControlButtonClass,
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                aria-label={`Zoom in. Current zoom level: ${Math.round(zoom)} of ${maxZoom}`}
                aria-keyshortcuts="+ ="
                data-testid="map-zoom-in"
              >
                <Plus size={iconSize} className={highContrastStyles} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Zoom In</p>
              <p className="text-xs text-muted-foreground">Press + or =</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Zoom Out Button */}
        {mergedConfig.showZoom && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomOut}
                disabled={zoom <= minZoom}
                className={cn(
                  buttonSize,
                  focusRingStyles,
                  'group',
                  mapControlButtonClass,
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                aria-label={`Zoom out. Current zoom level: ${Math.round(zoom)} of ${maxZoom}`}
                aria-keyshortcuts="- _"
                data-testid="map-zoom-out"
              >
                <Minus size={iconSize} className={highContrastStyles} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Zoom Out</p>
              <p className="text-xs text-muted-foreground">Press - or _</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Separator */}
        {mergedConfig.showZoom && (mergedConfig.showCompass || mergedConfig.showFitBounds || mergedConfig.showFullscreen) && (
          <div className={cn("h-px border-t mx-1", mapControlDividerClass)} role="separator" aria-hidden="true" />
        )}

        {/* Compass/North Indicator */}
        {mergedConfig.showCompass && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleResetNorth}
                disabled={Math.abs(bearing) < 0.1}
                className={cn(
                  buttonSize,
                  focusRingStyles,
                  'group',
                  mapControlButtonClass,
                  'disabled:opacity-50'
                )}
                aria-label={
                  Math.abs(bearing) > 0.1
                    ? `Reset map to face north. Currently rotated ${Math.round(bearing)} degrees`
                    : 'Map is facing north'
                }
                aria-keyshortcuts="n"
                data-testid="map-compass"
              >
                <Navigation2
                  size={iconSize}
                  style={{
                    transform: `rotate(${-bearing}deg)`,
                    transition: 'transform 200ms ease-out',
                  }}
                  className={cn(
                    Math.abs(bearing) > 0.1 ? 'text-primary' : 'text-muted-foreground'
                  )}
                  aria-hidden="true"
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{Math.abs(bearing) > 0.1 ? 'Reset to North' : 'Facing North'}</p>
              {Math.abs(bearing) > 0.1 && (
                <p className="text-xs text-muted-foreground">
                  Current: {Math.round(bearing)}° • Press N
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Fit to Bounds Button */}
        {mergedConfig.showFitBounds && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFitToBounds}
                disabled={totalDataPoints === 0}
                className={cn(
                  buttonSize,
                  focusRingStyles,
                  'group',
                  mapControlButtonClass,
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                aria-label={`Fit map to show all ${totalDataPoints} data points`}
                aria-keyshortcuts="f"
                aria-disabled={totalDataPoints === 0}
                data-testid="map-fit-bounds"
              >
                <Target size={iconSize} className={highContrastStyles} aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Fit to Data</p>
              <p className="text-xs text-muted-foreground">
                {totalDataPoints} data point{totalDataPoints !== 1 ? 's' : ''} • Press F
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Fullscreen Toggle */}
        {mergedConfig.showFullscreen && onFullscreenToggle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFullscreenToggle}
                className={cn(buttonSize, focusRingStyles, 'group', mapControlButtonClass)}
                aria-label={isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
                aria-pressed={isFullscreen}
                data-testid="map-fullscreen"
              >
                {isFullscreen ? (
                  <Minimize size={iconSize} className={highContrastStyles} aria-hidden="true" />
                ) : (
                  <Maximize size={iconSize} className={highContrastStyles} aria-hidden="true" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</p>
              <p className="text-xs text-muted-foreground">Press Esc to exit</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Keyboard shortcuts help (hidden, for screen readers) */}
        <div className="sr-only" role="note">
          Keyboard shortcuts: Plus or equals to zoom in, minus to zoom out,
          N to reset north, F to fit bounds, Escape to exit fullscreen.
          Use Tab to navigate between controls.
        </div>
      </div>
    </TooltipProvider>
  );
};

// ============================================================================
// Scale Indicator Component
// ============================================================================

export interface MapScaleIndicatorProps {
  /** Current zoom level */
  zoom: number;
  /** Map center latitude (for accurate scale calculation) */
  latitude?: number;
  /** Position on the map */
  position?: ControlPosition;
  /** Use metric units */
  metric?: boolean;
  /** Container width for responsive sizing */
  containerWidth?: number;
  /** Additional CSS class */
  className?: string;
}

/**
 * Calculate scale bar width and distance based on zoom level and latitude
 */
const calculateScale = (zoom: number, latitude: number = 0, metric: boolean = true) => {
  // Earth's circumference at equator in meters
  const earthCircumference = 40075016.686;

  // Calculate meters per pixel at current zoom and latitude
  const metersPerPixel = (earthCircumference * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom + 8);

  // Target scale bar width in pixels (between 50-150)
  const targetWidthPx = 100;
  const targetDistance = metersPerPixel * targetWidthPx;

  // Round to nice values
  const niceDistances = metric
    ? [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000]
    : [5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 26400, 52800, 105600, 264000, 528000, 1056000]; // in feet, then miles

  let bestDistance = niceDistances[0];
  for (const d of niceDistances) {
    if (d <= targetDistance * 1.5) {
      bestDistance = d;
    }
  }

  const widthPx = bestDistance / metersPerPixel;

  // Format distance label
  let label: string;
  if (metric) {
    if (bestDistance >= 1000) {
      label = `${bestDistance / 1000} km`;
    } else {
      label = `${bestDistance} m`;
    }
  } else {
    if (bestDistance >= 5280) {
      label = `${Math.round(bestDistance / 5280)} mi`;
    } else {
      label = `${bestDistance} ft`;
    }
  }

  return { widthPx: Math.min(Math.max(widthPx, 50), 150), label };
};

export const MapScaleIndicator: React.FC<MapScaleIndicatorProps> = ({
  zoom,
  latitude = 0,
  position = 'bottom-left',
  metric = true,
  containerWidth,
  className,
}) => {
  const { widthPx, label } = calculateScale(zoom, latitude, metric);

  // Determine if mobile
  const isMobile = containerWidth !== undefined && containerWidth <= BREAKPOINTS.mobile;

  const positionStyles = getPositionStyles(position);
  // Adjust position slightly for scale indicator
  if (position === 'bottom-left') {
    positionStyles.bottom = isMobile ? 20 : 35;
    positionStyles.left = 10;
  }

  return (
    <div
      className={cn(
        'flex flex-col items-start gap-0.5 px-1.5 py-1',
        mapControlSurfaceClass,
        isMobile && 'px-1 py-0.5',
        className
      )}
      style={positionStyles}
      data-testid="map-scale-indicator"
      role="img"
      aria-label={`Map scale: ${label}`}
    >
      <div
        className="h-1 bg-foreground border-l border-r border-foreground"
        style={{ width: widthPx }}
        aria-hidden="true"
      />
      {/* High contrast text - minimum 4.5:1 ratio */}
      <span
        className={cn(
          'font-semibold',
          isMobile ? 'text-[9px]' : 'text-[10px]'
        )}
        style={{ color: mapControlText.primary }}
      >
        {label}
      </span>
    </div>
  );
};

export default MapNavigationControls;
